import { useState } from "react";
import classNames from "classnames";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import {
  canCenterRecommended,
  guideSourceOf,
  guideSources,
  zoneChoices,
  zoneCurrent,
  zoneValue,
} from "./guide-choices";
import { GUIDE_KINDS, GUIDE_STEPS } from "./screen-groups";
import { fillTemplate, slotLabel } from "./workshop-view";

const STEPS = [GUIDE_STEPS.SOURCE, GUIDE_STEPS.ZONES, GUIDE_STEPS.DONE];

const WorkshopGuide = ({
  guide,
  labels,
  fieldLabels,
  collections,
  registry,
  actions,
  disabled,
  onSource,
  onChoose,
  onMove,
  onFinish,
  onRecommendedCenter,
}) => {
  const current = guideSourceOf(guide);
  const [picked, setPicked] = useState(current);
  if (guide.missing)
    return <p className="workshop-guide__note">{labels.MISSING}</p>;

  const isList = guide.kind === GUIDE_KINDS.LIST;
  const stepIndex = STEPS.indexOf(guide.step);
  const zone = guide.zones[guide.zone] || null;
  const choicesOf = (slot) =>
    zoneChoices({
      guide,
      slot,
      labels,
      fieldLabels,
      collections,
      registry,
      actions,
    });
  const sampleOf = (slot) => slotLabel(slot, fieldLabels);

  const renderSource = () => (
    <div className="workshop-guide__step">
      <strong className="workshop-guide__ask">
        {isList ? labels.LIST_ASK : labels.CARD_ASK}
      </strong>
      <span className="workshop-guide__note">
        {isList ? labels.LIST_NOTE : labels.CARD_NOTE}
      </span>
      <div className="workshop-guide__choices">
        {guideSources({ guide, collections, labels }).map((source) => (
          <button
            key={source.key}
            type="button"
            aria-pressed={picked === source.key}
            className="workshop-guide__choice"
            disabled={disabled}
            onClick={() => setPicked(source.key)}
          >
            <span className="workshop-guide__choice-label">{source.label}</span>
            <span className="workshop-guide__choice-hint">
              {fillTemplate(labels.INFOS, { count: source.count })}
            </span>
          </button>
        ))}
      </div>
      {isList && !isNil(current) && !isNil(picked) && picked !== current ? (
        <span className="workshop-guide__warning">
          {labels.LIST_SWITCH_NOTE}
        </span>
      ) : null}
      <button
        type="button"
        className="workshop-guide__primary"
        disabled={disabled || isNil(picked)}
        onClick={() => onSource(picked)}
      >
        {labels.NEXT}
      </button>
    </div>
  );

  const renderZone = () => {
    if (isNil(zone))
      return <p className="workshop-guide__note">{labels.NO_ZONES}</p>;
    const selected = zoneCurrent(zone);
    return (
      <div className="workshop-guide__step">
        <span className="workshop-guide__count">
          {fillTemplate(labels.ZONE_COUNT, {
            number: guide.zone + 1,
            total: guide.zones.length,
          })}
        </span>
        <strong className="workshop-guide__ask">
          {fillTemplate(labels.ZONE_ASK, { sample: sampleOf(zone) })}
        </strong>
        <span className="workshop-guide__note">{labels.ZONE_HINT}</span>
        <div className="workshop-guide__choices">
          {choicesOf(zone).map((choice) => (
            <button
              key={choice.key}
              type="button"
              aria-pressed={selected === choice.key}
              className="workshop-guide__choice"
              disabled={disabled}
              onClick={() => onChoose(guide.zone, choice)}
            >
              <span className="workshop-guide__choice-label">
                {choice.label}
              </span>
            </button>
          ))}
        </div>
        <div className="workshop-guide__actions">
          <button
            type="button"
            className="workshop-guide__secondary"
            disabled={disabled}
            onClick={() =>
              guide.zone > 0
                ? onMove({ step: GUIDE_STEPS.ZONES, zone: guide.zone - 1 })
                : onMove({ step: GUIDE_STEPS.SOURCE })
            }
          >
            {labels.BACK}
          </button>
          <button
            type="button"
            className="workshop-guide__secondary"
            disabled={disabled}
            onClick={() => onMove({ step: GUIDE_STEPS.DONE })}
          >
            {labels.SUMMARY}
          </button>
        </div>
      </div>
    );
  };

  const renderDone = () => (
    <div className="workshop-guide__step">
      <strong className="workshop-guide__ask">{labels.DONE_TITLE}</strong>
      {isList && canCenterRecommended(guide.collection) ? (
        <label className="workshop-guide__option">
          <input
            type="checkbox"
            checked={Boolean(guide.recommendedCenter)}
            disabled={disabled}
            onChange={(event) => onRecommendedCenter(event.target.checked)}
          />
          <span className="workshop-guide__option-text">
            <span className="workshop-guide__choice-label">
              {labels.RECOMMENDED_CENTER}
            </span>
            <span className="workshop-guide__choice-hint">
              {labels.RECOMMENDED_CENTER_HINT}
            </span>
          </span>
        </label>
      ) : null}
      {guide.zones.length ? (
        <div className="workshop-guide__summary">
          {guide.zones.map((slot, index) => {
            const value = zoneValue({
              slot,
              choices: choicesOf(slot),
              labels,
            });
            return (
              <button
                key={slot.id}
                type="button"
                className="workshop-guide__summary-row"
                disabled={disabled}
                onClick={() => onMove({ step: GUIDE_STEPS.ZONES, zone: index })}
              >
                <span className="workshop-guide__summary-sample">
                  {sampleOf(slot)}
                </span>
                <span
                  className={classNames("workshop-guide__summary-value", {
                    "workshop-guide__summary-value--unset": !value.set,
                  })}
                >
                  {value.text}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="workshop-guide__note">{labels.NO_ZONES}</p>
      )}
      <div className="workshop-guide__actions">
        <button
          type="button"
          className="workshop-guide__secondary"
          disabled={disabled}
          onClick={() => onMove({ step: GUIDE_STEPS.SOURCE })}
        >
          {labels.CHANGE}
        </button>
        <button
          type="button"
          className="workshop-guide__primary"
          onClick={onFinish}
        >
          {labels.FINISH}
        </button>
      </div>
    </div>
  );

  const views = {
    [GUIDE_STEPS.SOURCE]: renderSource,
    [GUIDE_STEPS.ZONES]: renderZone,
    [GUIDE_STEPS.DONE]: renderDone,
  };

  return (
    <div className="workshop-guide">
      <ol className="workshop-guide__steps">
        {STEPS.map((step, index) => (
          <li
            key={step}
            className={classNames("workshop-guide__steps-item", {
              "workshop-guide__steps-item--done": index < stepIndex,
              "workshop-guide__steps-item--on": index === stepIndex,
            })}
          >
            {labels.STEPS[step.toUpperCase()]}
          </li>
        ))}
      </ol>
      {views[guide.step]()}
    </div>
  );
};

WorkshopGuide.propTypes = {
  guide: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    step: PropTypes.string.isRequired,
    zone: PropTypes.number,
    zones: PropTypes.array,
    missing: PropTypes.bool,
    recommendedCenter: PropTypes.bool,
  }).isRequired,
  labels: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  collections: PropTypes.array,
  registry: PropTypes.array,
  actions: PropTypes.array,
  disabled: PropTypes.bool,
  onSource: PropTypes.func.isRequired,
  onChoose: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onRecommendedCenter: PropTypes.func,
};

WorkshopGuide.defaultProps = {
  collections: [],
  registry: [],
  actions: [],
  disabled: false,
  onRecommendedCenter: () => null,
};

export default WorkshopGuide;
